import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import './FileConverter.css'
import AdSlot from './AdSlot'
import { OUTPUT_FORMATS, convertImage } from './utils/imageFileConverter'
import { parseCsv, rowsToCsv, rowsToXml, xmlToRows, rowsFromJson } from './utils/dataFormatConverter'
import { imagesToPdf } from './utils/imageToPdf'
import { parseXlsxFile, rowsToXlsxBlob } from './utils/xlsxConverter'
import { VIDEO_FORMATS, AUDIO_FORMATS, convertMediaFile } from './utils/mediaConvertApiClient'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const DOWNLOAD_WAIT_SECONDS = 8

const MODES = [
  { key: 'imagem', label: 'Imagem (PNG/JPG/WebP)' },
  { key: 'dados', label: 'Dados (CSV/JSON/XML/XLSX)' },
  { key: 'imagem-pdf', label: 'Imagens → PDF' },
  { key: 'video-audio', label: 'Vídeo/Áudio (no servidor)' },
]

const ALL_DATA_FORMATS = ['csv', 'json', 'xml', 'xlsx']

const TEXT_MIME = { json: 'application/json', xml: 'application/xml', csv: 'text/csv' }

const rowsFromText = (text, format) => {
  if (format === 'csv') return parseCsv(text)
  if (format === 'json') return rowsFromJson(text)
  return xmlToRows(text)
}

const rowsToText = (rows, format) => {
  if (format === 'csv') return rowsToCsv(rows)
  if (format === 'json') return JSON.stringify(rows, null, 2)
  return rowsToXml(rows)
}

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

const useDownloadGate = () => {
  const [waitSeconds, setWaitSeconds] = useState(0)
  useEffect(() => {
    if (waitSeconds <= 0) return undefined
    const timeoutId = setTimeout(() => setWaitSeconds((s) => s - 1), 1000)
    return () => clearTimeout(timeoutId)
  }, [waitSeconds])
  const startGate = () => setWaitSeconds(DOWNLOAD_WAIT_SECONDS)
  return [waitSeconds, startGate]
}

const downloadGateUi = (waitSeconds, onDownload) => (
  <div className="file-converter-download-gate">
    <AdSlot slot="9857175260" />
    <button
      type="button"
      className="tool-button"
      onClick={onDownload}
      disabled={waitSeconds > 0}
    >
      {waitSeconds > 0 ? `Preparando download... ${waitSeconds}s` : 'Baixar arquivo convertido'}
    </button>
  </div>
)

const ImageModeSection = () => {
  const [original, setOriginal] = useState(null)
  const [targetFormat, setTargetFormat] = useState('png')
  const [converted, setConverted] = useState(null)
  const [waitSeconds, startGate] = useDownloadGate()
  const imageRef = useRef(null)

  const handleFile = (event) => {
    const file = event.target.files[0]
    if (!file) return
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      imageRef.current = img
      setOriginal({ url, size: file.size, name: file.name })
      setConverted(null)
    }
    img.src = url
  }

  const convert = async () => {
    if (!imageRef.current) return
    try {
      const result = await convertImage(imageRef.current, targetFormat)
      setConverted(result)
      startGate()
      trackEvent('tool_generate', { tool: 'file_converter', mode: 'imagem', format: targetFormat })
    } catch {
      setConverted(null)
    }
  }

  const download = () => {
    if (!converted || waitSeconds > 0) return
    const baseName = original.name.replace(/\.[^.]+$/, '')
    const link = document.createElement('a')
    link.href = converted.url
    link.download = `${baseName}.${converted.format.extension}`
    link.click()
    trackEvent('tool_copy', { tool: 'file_converter' })
  }

  return (
    <>
      <div className="tool-field">
        <label htmlFor="image-file-input">Escolha uma imagem</label>
        <input
          id="image-file-input"
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/bmp"
          onChange={handleFile}
        />
      </div>

      {original && (
        <>
          <div className="tool-field">
            <label htmlFor="target-format">Converter para</label>
            <select
              id="target-format"
              value={targetFormat}
              onChange={(e) => setTargetFormat(e.target.value)}
            >
              {OUTPUT_FORMATS.map((format) => (
                <option key={format.key} value={format.key}>
                  {format.label}
                </option>
              ))}
            </select>
          </div>

          <button type="button" className="tool-button" onClick={convert}>
            Converter
          </button>

          <div className="file-converter-compare">
            <div className="file-converter-item">
              <span className="tool-result-label">Original: {formatSize(original.size)}</span>
              <img src={original.url} alt="Original" />
            </div>

            {converted && (
              <div className="file-converter-item">
                <span className="tool-result-label">
                  {converted.format.label}: {formatSize(converted.size)}
                </span>
                <img src={converted.url} alt="Convertida" />
                {downloadGateUi(waitSeconds, download)}
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}

const DataModeSection = () => {
  const [fromFormat, setFromFormat] = useState('csv')
  const [toFormat, setToFormat] = useState('json')
  const [input, setInput] = useState('nome,idade\nAna,30\nBruno,25')
  const [xlsxFile, setXlsxFile] = useState(null)
  const [output, setOutput] = useState('')
  const [xlsxBlobUrl, setXlsxBlobUrl] = useState(null)
  const [error, setError] = useState('')
  const [waitSeconds, startGate] = useDownloadGate()

  const handleTextFile = (event) => {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => setInput(String(reader.result))
    reader.readAsText(file)
    event.target.value = ''
  }

  const handleXlsxFile = (event) => {
    const file = event.target.files[0]
    if (file) setXlsxFile(file)
  }

  const convert = async () => {
    setOutput('')
    setXlsxBlobUrl(null)
    try {
      const rows = fromFormat === 'xlsx' ? await parseXlsxFile(xlsxFile) : rowsFromText(input, fromFormat)

      if (toFormat === 'xlsx') {
        setXlsxBlobUrl(URL.createObjectURL(rowsToXlsxBlob(rows)))
      } else {
        setOutput(rowsToText(rows, toFormat))
      }
      setError('')
      startGate()
      trackEvent('tool_generate', { tool: 'file_converter', mode: 'dados', from: fromFormat, to: toFormat })
    } catch {
      setError(
        fromFormat === 'xlsx'
          ? 'Não foi possível ler essa planilha.'
          : `Não foi possível converter, confira se o texto está em ${fromFormat.toUpperCase()} válido.`,
      )
    }
  }

  const download = () => {
    if (waitSeconds > 0) return
    if (toFormat === 'xlsx') {
      if (!xlsxBlobUrl) return
      const link = document.createElement('a')
      link.href = xlsxBlobUrl
      link.download = 'convertido.xlsx'
      link.click()
    } else {
      if (!output) return
      const blob = new Blob([output], { type: TEXT_MIME[toFormat] })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `convertido.${toFormat}`
      link.click()
      URL.revokeObjectURL(url)
    }
    trackEvent('tool_copy', { tool: 'file_converter' })
  }

  const hasResult = toFormat === 'xlsx' ? !!xlsxBlobUrl : !!output
  const canConvert = fromFormat === 'xlsx' ? !!xlsxFile : true

  return (
    <>
      <div className="tool-row">
        <div className="tool-field">
          <label htmlFor="from-format">De</label>
          <select id="from-format" value={fromFormat} onChange={(e) => setFromFormat(e.target.value)}>
            {ALL_DATA_FORMATS.map((f) => (
              <option key={f} value={f}>
                {f.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
        <div className="tool-field">
          <label htmlFor="to-format">Para</label>
          <select id="to-format" value={toFormat} onChange={(e) => setToFormat(e.target.value)}>
            {ALL_DATA_FORMATS.map((f) => (
              <option key={f} value={f}>
                {f.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
      </div>

      {fromFormat === 'xlsx' ? (
        <div className="tool-field">
          <label htmlFor="xlsx-file-input">Planilha (.xlsx)</label>
          <input id="xlsx-file-input" type="file" accept=".xlsx" onChange={handleXlsxFile} />
          {xlsxFile && <span className="tool-import-hint">{xlsxFile.name} selecionado</span>}
        </div>
      ) : (
        <>
          <div className="tool-import-row">
            <label htmlFor="data-file-input" className="tool-button tool-button-secondary" style={{ cursor: 'pointer' }}>
              Importar arquivo
            </label>
            <input id="data-file-input" type="file" accept=".csv,.json,.xml,.txt" onChange={handleTextFile} hidden />
          </div>

          <div className="tool-field">
            <label htmlFor="data-input">Conteúdo ({fromFormat.toUpperCase()})</label>
            <textarea id="data-input" rows="8" value={input} onChange={(e) => setInput(e.target.value)} />
          </div>
        </>
      )}

      <button type="button" className="tool-button" onClick={convert} disabled={!canConvert}>
        Converter
      </button>

      {error && <p className="tool-error">{error}</p>}

      {hasResult && (
        <div className="tool-field" style={{ marginTop: 20 }}>
          {toFormat === 'xlsx' ? (
            <p className="tool-import-hint">Planilha pronta pra baixar.</p>
          ) : (
            <>
              <label htmlFor="data-output">Resultado ({toFormat.toUpperCase()})</label>
              <textarea id="data-output" rows="8" readOnly value={output} />
            </>
          )}
          {downloadGateUi(waitSeconds, download)}
        </div>
      )}
    </>
  )
}

const ImageToPdfSection = () => {
  const [files, setFiles] = useState([])
  const [pdfUrl, setPdfUrl] = useState(null)
  const [error, setError] = useState('')
  const [converting, setConverting] = useState(false)
  const [waitSeconds, startGate] = useDownloadGate()

  const handleFiles = (event) => {
    setFiles(Array.from(event.target.files))
    setPdfUrl(null)
  }

  const convert = async () => {
    if (files.length === 0) return
    setConverting(true)
    setError('')
    try {
      const blob = await imagesToPdf(files)
      setPdfUrl(URL.createObjectURL(blob))
      startGate()
      trackEvent('tool_generate', { tool: 'file_converter', mode: 'imagem-pdf', count: files.length })
    } catch {
      setError('Não foi possível gerar o PDF a partir dessas imagens.')
    } finally {
      setConverting(false)
    }
  }

  const download = () => {
    if (!pdfUrl || waitSeconds > 0) return
    const link = document.createElement('a')
    link.href = pdfUrl
    link.download = 'imagens-convertidas.pdf'
    link.click()
    trackEvent('tool_copy', { tool: 'file_converter' })
  }

  return (
    <>
      <div className="tool-field">
        <label htmlFor="pdf-file-input">Escolha uma ou mais imagens (uma página por imagem)</label>
        <input
          id="pdf-file-input"
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/bmp"
          multiple
          onChange={handleFiles}
        />
      </div>

      {files.length > 0 && (
        <p className="tool-import-hint">{files.length} imagem(ns) selecionada(s)</p>
      )}

      <button type="button" className="tool-button" onClick={convert} disabled={files.length === 0 || converting}>
        {converting ? 'Gerando PDF...' : 'Gerar PDF'}
      </button>

      {error && <p className="tool-error">{error}</p>}

      {pdfUrl && downloadGateUi(waitSeconds, download)}
    </>
  )
}

const VideoAudioModeSection = () => {
  const [file, setFile] = useState(null)
  const [kind, setKind] = useState(null) // 'video' | 'audio' | null (unrecognized)
  const [targetFormat, setTargetFormat] = useState('mp4')
  const [resultBlob, setResultBlob] = useState(null)
  const [resultUrl, setResultUrl] = useState(null)
  const [converting, setConverting] = useState(false)
  const [error, setError] = useState('')
  const [waitSeconds, startGate] = useDownloadGate()

  const handleFile = (event) => {
    const selected = event.target.files[0]
    if (!selected) return
    setError('')
    setResultBlob(null)
    setResultUrl(null)
    setFile(selected)

    if (selected.type.startsWith('video/')) {
      setKind('video')
      setTargetFormat('mp4')
    } else if (selected.type.startsWith('audio/')) {
      setKind('audio')
      setTargetFormat('mp3')
    } else {
      setKind(null)
    }
  }

  const formatOptions = kind === 'audio' ? AUDIO_FORMATS : VIDEO_FORMATS

  const convert = async () => {
    if (!file || !kind) return
    setConverting(true)
    setError('')
    try {
      const blob = await convertMediaFile(file, targetFormat)
      setResultBlob(blob)
      setResultUrl(URL.createObjectURL(blob))
      startGate()
      trackEvent('tool_generate', { tool: 'file_converter', mode: 'video-audio', format: targetFormat })
    } catch (err) {
      setError(err.message || 'Não foi possível converter esse arquivo agora.')
    } finally {
      setConverting(false)
    }
  }

  const download = () => {
    if (!resultUrl || waitSeconds > 0) return
    const link = document.createElement('a')
    link.href = resultUrl
    link.download = `convertido.${targetFormat}`
    link.click()
    trackEvent('tool_copy', { tool: 'file_converter' })
  }

  return (
    <>
      <p className="tool-import-hint">
        Esse modo envia o arquivo pro servidor pra converter (diferente
        dos outros modos aqui, que processam tudo no navegador). Limite de
        80MB, o arquivo é apagado do servidor assim que a conversão
        termina.
      </p>

      <div className="tool-field">
        <label htmlFor="media-file-input">Escolha um vídeo ou áudio</label>
        <input
          id="media-file-input"
          type="file"
          accept="video/*,audio/*"
          onChange={handleFile}
        />
      </div>

      {file && kind === null && (
        <p className="tool-error">Esse tipo de arquivo não é reconhecido como vídeo ou áudio.</p>
      )}

      {file && kind && (
        <>
          <p className="tool-import-hint">{file.name} — {formatSize(file.size)}</p>

          <div className="tool-field">
            <label htmlFor="media-target-format">Converter para</label>
            <select
              id="media-target-format"
              value={targetFormat}
              onChange={(e) => setTargetFormat(e.target.value)}
            >
              {formatOptions.map((format) => (
                <option key={format.key} value={format.key}>
                  {format.label}
                </option>
              ))}
            </select>
          </div>

          <button type="button" className="tool-button" onClick={convert} disabled={converting}>
            {converting ? 'Convertendo... pode levar um tempo' : 'Converter'}
          </button>
        </>
      )}

      {error && <p className="tool-error">{error}</p>}

      {resultBlob && (
        <div className="tool-field" style={{ marginTop: 20 }}>
          <p className="tool-import-hint">Convertido: {formatSize(resultBlob.size)}</p>
          {downloadGateUi(waitSeconds, download)}
        </div>
      )}
    </>
  )
}

const FileConverter = () => {
  useDocumentMeta({
    title: 'Conversor de Arquivos | jarder.dev',
    description:
      'Converta imagens (PNG/JPG/WebP), dados (CSV/JSON/XML/XLSX), ' +
      'vídeo e áudio. Grátis.',
  })

  const [mode, setMode] = useState('imagem')

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Conversor de Arquivos</h1>
      <p className="tool-disclaimer">
        Imagem, dados tabulares (CSV/JSON/XML/XLSX) e criação de PDF a
        partir de imagens são processados direto no seu navegador, o
        arquivo nunca sai do seu computador. Vídeo/áudio precisa de
        processamento pesado demais pro navegador aguentar, então esse
        modo específico envia o arquivo pro servidor — veja o aviso
        dentro dele antes de usar.
      </p>

      <div className="tool-row" style={{ marginBottom: 20 }}>
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            className={`tool-button ${mode === m.key ? '' : 'tool-button-secondary'}`}
            onClick={() => setMode(m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'imagem' && <ImageModeSection />}
      {mode === 'dados' && <DataModeSection />}
      {mode === 'imagem-pdf' && <ImageToPdfSection />}
      {mode === 'video-audio' && <VideoAudioModeSection />}

      <div className="tool-info-section">
        <h2>Quando usar cada modo</h2>
        <p>
          <strong>Imagem</strong> converte entre PNG, JPG e WebP mantendo
          a resolução original  útil quando um site só aceita
          determinado formato. <strong>Dados</strong> transforma
          planilhas e arquivos estruturados entre CSV, JSON, XML e XLSX,
          comum ao migrar dados entre sistemas ou preparar uma importação.{' '}
          <strong>Imagens → PDF</strong> junta várias fotos em um único
          PDF, uma página por imagem. Já o modo{' '}
          <strong>Vídeo/Áudio</strong> depende de processamento pesado
          demais para rodar no navegador, por isso é o único que envia o
          arquivo para conversão no servidor.
        </p>
      </div>
    </div>
  )
}

export default FileConverter
